import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus, Users, Receipt, Trash2, Wallet, LogOut, Crown, MailWarning, Megaphone, X, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useChit } from "@/context/ChitContext";
import { useAuth } from "@/context/AuthContext";
import { AddGroupDrawer } from "@/components/AddGroupDrawer";
import { Avatar } from "@/components/Avatar";
import { formateazaRON } from "@/lib/settlement";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { groups, deleteGroup, loading } = useChit();
  const { user, logout, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [verifDismissed, setVerifDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [announcement, setAnnouncement] = useState(null);

  // Anunț de la Fondatori (banner in-app, dismisibil per-anunț via localStorage).
  useEffect(() => {
    axios
      .get(`${API}/announcement/active`, { withCredentials: true })
      .then((r) => {
        const a = r.data.announcement;
        if (a && localStorage.getItem("chit_dismissed_announcement") !== a.id) {
          setAnnouncement(a);
        }
      })
      .catch(() => {});
  }, []);

  const dismissAnnouncement = () => {
    if (announcement) localStorage.setItem("chit_dismissed_announcement", announcement.id);
    setAnnouncement(null);
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success("Ți-am retrimis emailul de confirmare. Verifică inboxul (și Spam, dacă e cazul).");
    } catch {
      toast.error("Nu am putut retrimite emailul. Încearcă din nou.");
    } finally {
      setResending(false);
    }
  };

  const totalGrup = (g) => g.expenses.reduce((s, e) => s + e.amount, 0);

  // Pluralizare corectă în română (1 membru / 2 membri, 1 cheltuială / 2 cheltuieli).
  const textMembri = (n) => (n === 1 ? "1 membru" : `${n} membri`);
  const textCheltuieli = (n) => (n === 1 ? "1 cheltuială" : `${n} cheltuieli`);

  return (
    <div className="min-h-screen pb-28">
      {/* Header lipicios cu efect de sticlă */}
      <header className="sticky top-0 z-20 backdrop-blur-lg bg-background/80 border-b border-slate-100 px-5 py-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <img
                src="/chit-logo.png"
                alt="Chit"
                data-testid="app-logo"
                className="h-9"
              />
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {user?.name ? `Salut, ${user.name.split(" ")[0]}!` : "Împarte cheltuielile, fii chit"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.name && <Avatar name={user.name} size="sm" />}
            {user?.isFounder && (
              <button
                data-testid="founder-badge-home"
                title="Panou Fondator"
                onClick={() => navigate("/admin")}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-amber-50 text-amber-500 active:scale-90 transition-transform"
              >
                <BadgeCheck className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
            <button
              data-testid="premium-btn"
              aria-label="Chit Premium"
              onClick={() => navigate("/premium")}
              className="h-9 w-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors"
            >
              <Crown className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <button
              data-testid="logout-btn"
              aria-label="Deconectare"
              onClick={logout}
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
            >
              <LogOut className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Banner: confirmă-ți emailul (doar pt. conturi email/parolă neverificate) */}
      {user && user.emailVerified === false && !verifDismissed && (
        <div
          data-testid="verify-email-banner"
          className="mx-5 mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 flex items-start gap-3"
        >
          <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <MailWarning className="h-4 w-4 text-amber-600" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-800">Confirmă-ți emailul</p>
            <p className="text-xs text-amber-700/80 mt-0.5">
              Ți-am trimis un link de confirmare pe {user.email}. Nu-l vezi în Inbox? Verifică și
              folderul Spam sau Promoții.
            </p>
            <button
              data-testid="resend-verification-btn"
              onClick={handleResendVerification}
              disabled={resending}
              className="mt-2 text-xs font-bold text-amber-700 underline underline-offset-2 disabled:opacity-60"
            >
              {resending ? "Se trimite..." : "Retrimite emailul"}
            </button>
          </div>
          <button
            data-testid="verify-banner-dismiss"
            onClick={() => setVerifDismissed(true)}
            aria-label="Închide"
            className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-amber-100 text-amber-600 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Banner: anunț global de la Fondatori */}
      {announcement && (
        <div
          data-testid="announcement-banner"
          className="mx-5 mt-4 rounded-2xl p-3.5 flex items-start gap-3 text-white"
          style={{ backgroundColor: "#0f1b2d" }}
        >
          <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#12b981" }}>
            <Megaphone className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p data-testid="announcement-banner-message" className="text-sm font-medium leading-snug">
              {announcement.message}
            </p>
          </div>
          <button
            data-testid="announcement-banner-dismiss"
            onClick={dismissAnnouncement}
            aria-label="Închide"
            className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="px-5 pt-5">
        {loading ? (
          <p className="text-center text-slate-400 text-sm mt-24">Se încarcă...</p>
        ) : groups.length === 0 ? (
          // Stare goală prietenoasă
          <div className="flex flex-col items-center justify-center text-center mt-24 px-6">
            <div className="h-20 w-20 rounded-3xl bg-emerald-100 flex items-center justify-center mb-5">
              <Wallet className="h-10 w-10 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-800">
              Niciun grup încă
            </h2>
            <p className="text-slate-500 text-sm mt-2 max-w-xs">
              Creează primul grup, adaugă prietenii și începe să împarți
              cheltuielile.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`group-card-${g.id}`}
                onClick={() => navigate(`/grup/${g.id}`)}
                className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 cursor-pointer hover:-translate-y-1 transition-transform active:scale-[0.98]"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg font-bold text-slate-900 truncate">
                      {g.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-slate-500 text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" strokeWidth={2.5} />
                        {textMembri(g.members.length)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Receipt className="h-4 w-4" strokeWidth={2.5} />
                        {textCheltuieli(g.expenses.length)}
                      </span>
                    </div>
                  </div>
                  {g.isOwner ? (
                    <button
                      data-testid={`delete-group-btn-${g.id}`}
                      aria-label="Șterge grupul"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDelete(g);
                      }}
                      className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  ) : (
                    <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                      partajat
                    </span>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-50">
                  <p className="text-xs text-slate-400 font-medium">
                    Total cheltuit
                  </p>
                  <p className="font-heading text-xl font-extrabold text-slate-800">
                    {formateazaRON(totalGrup(g))}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Buton flotant (FAB) pentru grup nou */}
      <div className="fixed bottom-6 left-0 right-0 z-30 px-5 max-w-md mx-auto">
        <button
          data-testid="add-group-fab"
          onClick={() => setDrawerOpen(true)}
          className="w-full h-14 rounded-2xl brand-bg brand-shadow text-white font-bold text-base shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Plus className="h-6 w-6" strokeWidth={2.5} />
          Grup nou
        </button>
      </div>

      <AddGroupDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />

      {/* Confirmare ștergere grup */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="max-w-[340px] rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi grupul?</AlertDialogTitle>
            <AlertDialogDescription>
              „{toDelete?.name}" și toate cheltuielile lui vor fi șterse
              definitiv.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Anulează</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-group-btn"
              onClick={() => {
                deleteGroup(toDelete.id);
                setToDelete(null);
              }}
              className="rounded-xl bg-rose-500 hover:bg-rose-600"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
